// src/pages/finance-portal/depreciation/DepreciationAssets.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem, Grid,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    IconButton, Tooltip,
} from '@mui/material';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import AddIcon from '@mui/icons-material/Add';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import axiosInstance from '../../../utils/axiosInstance';
import { useSnackbar } from 'notistack';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    DISPOSED: { bg: '#fecaca', color: '#991b1b' },
    FULLY_DEPRECIATED: { bg: '#f1f5f9', color: '#475569' },
};

const DEPRECIATION_METHODS = [
    { value: 'STRAIGHT_LINE', label: 'Straight Line' },
    { value: 'DECLINING_BALANCE', label: 'Declining Balance' },
    { value: 'DOUBLE_DECLINING', label: 'Double Declining' },
    { value: 'SUM_OF_YEARS', label: 'Sum of Years' },
    { value: 'MACRS', label: 'MACRS' },
];

const STATUSES = [
    { value: '', label: 'All Statuses' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'DISPOSED', label: 'Disposed' },
    { value: 'FULLY_DEPRECIATED', label: 'Fully Depreciated' },
];

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function DepreciationAssets() {
    const { enqueueSnackbar } = useSnackbar();
    const [assets, setAssets] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [filterStatus, setFilterStatus] = useState('');
    const [taxYear, setTaxYear] = useState(new Date().getFullYear().toString());

    // Summary
    const [summary, setSummary] = useState({ total_cost: 0, total_depreciation: 0, total_book_value: 0 });

    // Create dialog
    const [formOpen, setFormOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        asset_name: '', original_cost: '', date_placed_in_service: '',
        depreciation_method: 'STRAIGHT_LINE', useful_life_years: '',
        salvage_value: '', section_179_amount: '', bonus_depreciation_percent: '',
        gl_asset_account: '', gl_depreciation_account: '', gl_expense_account: '',
    });

    // Dispose dialog
    const [disposeOpen, setDisposeOpen] = useState(false);
    const [disposingId, setDisposingId] = useState(null);
    const [disposeData, setDisposeData] = useState({ disposal_amount: '', date_disposed: '' });
    const [disposing, setDisposing] = useState(false);

    const fetchAssets = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (filterStatus) params.status = filterStatus;
            if (taxYear) params.tax_year = taxYear;
            const res = await axiosInstance.get('/api/finance/tax/depreciation', { params });
            if (res.data.success) {
                setAssets(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load assets');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, filterStatus, taxYear]);

    const fetchSummary = useCallback(async () => {
        try {
            const params = {};
            if (taxYear) params.tax_year = taxYear;
            const res = await axiosInstance.get('/api/finance/tax/depreciation/summary', { params });
            if (res.data.success) {
                setSummary(res.data.data || { total_cost: 0, total_depreciation: 0, total_book_value: 0 });
            }
        } catch { /* silent */ }
    }, [taxYear]);

    useEffect(() => { fetchAssets(); fetchSummary(); }, [fetchAssets, fetchSummary]);

    const openCreateForm = () => {
        setFormData({
            asset_name: '', original_cost: '', date_placed_in_service: '',
            depreciation_method: 'STRAIGHT_LINE', useful_life_years: '',
            salvage_value: '', section_179_amount: '', bonus_depreciation_percent: '',
            gl_asset_account: '', gl_depreciation_account: '', gl_expense_account: '',
        });
        setFormOpen(true);
    };

    const handleCreate = async () => {
        setSaving(true);
        try {
            const payload = {
                ...formData,
                original_cost: parseFloat(formData.original_cost) || 0,
                useful_life_years: parseInt(formData.useful_life_years, 10) || 0,
                salvage_value: parseFloat(formData.salvage_value) || 0,
                section_179_amount: parseFloat(formData.section_179_amount) || 0,
                bonus_depreciation_percent: parseFloat(formData.bonus_depreciation_percent) || 0,
            };
            await axiosInstance.post('/api/finance/tax/depreciation', payload);
            enqueueSnackbar('Asset created', { variant: 'success' });
            setFormOpen(false);
            fetchAssets();
            fetchSummary();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to create asset', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCalculate = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/tax/depreciation/${id}/calculate`);
            enqueueSnackbar('Depreciation calculated', { variant: 'success' });
            fetchAssets();
            fetchSummary();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Calculation failed', { variant: 'error' });
        }
    };

    const openDisposeDialog = (asset) => {
        setDisposingId(asset.id);
        setDisposeData({ disposal_amount: '', date_disposed: '' });
        setDisposeOpen(true);
    };

    const handleDispose = async () => {
        setDisposing(true);
        try {
            const payload = {
                disposal_amount: parseFloat(disposeData.disposal_amount) || 0,
                date_disposed: disposeData.date_disposed,
            };
            await axiosInstance.post(`/api/finance/tax/depreciation/${disposingId}/dispose`, payload);
            enqueueSnackbar('Asset disposed', { variant: 'success' });
            setDisposeOpen(false);
            fetchAssets();
            fetchSummary();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to dispose asset', { variant: 'error' });
        } finally {
            setDisposing(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PrecisionManufacturingIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Depreciation Assets</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Track asset depreciation schedules</Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreateForm}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: ACCENT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        Add Asset
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                        { label: 'Total Cost', value: summary.total_cost, color: '#1e40af' },
                        { label: 'Total Depreciation', value: summary.total_depreciation, color: '#92400e' },
                        { label: 'Total Book Value', value: summary.total_book_value, color: '#166534' },
                    ].map((card, idx) => (
                        <Grid item xs={12} sm={4} key={idx}>
                            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 2.5 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>{card.label}</Typography>
                                <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: card.color, mt: 0.5 }}>{formatCurrency(card.value)}</Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small" label="Tax Year" type="number" value={taxYear}
                        onChange={e => { setTaxYear(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        sx={{ minWidth: 110 }}
                    />
                    <TextField
                        size="small" select label="Status" value={filterStatus}
                        onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 180 }}
                    >
                        {STATUSES.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontFamily }}>{s.label}</MenuItem>)}
                    </TextField>
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Asset Name', 'Cost', 'Method', 'Life (Yrs)', 'Placed in Service', 'Book Value', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                    ))
                                ) : assets.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No assets found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : assets.map(asset => (
                                    <TableRow key={asset.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{asset.asset_name}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatCurrency(asset.original_cost)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{getStatusLabel(asset.depreciation_method)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{asset.useful_life_years || '\u2014'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatDate(asset.date_placed_in_service)}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{formatCurrency(asset.book_value)}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip label={getStatusLabel(asset.status)} size="small"
                                                sx={{
                                                    fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                    bgcolor: (STATUS_COLORS[asset.status] || STATUS_COLORS.ACTIVE).bg,
                                                    color: (STATUS_COLORS[asset.status] || STATUS_COLORS.ACTIVE).color,
                                                }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {asset.status === 'ACTIVE' && (
                                                    <>
                                                        <Tooltip title="Calculate Depreciation">
                                                            <IconButton size="small" onClick={() => handleCalculate(asset.id)}>
                                                                <CalculateIcon sx={{ fontSize: 18, color: '#1e40af' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Dispose">
                                                            <IconButton size="small" onClick={() => openDisposeDialog(asset)}>
                                                                <DeleteSweepIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{ '& *': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Create Asset Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Add Depreciation Asset</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Asset Name" required
                                value={formData.asset_name}
                                onChange={e => setFormData(prev => ({ ...prev, asset_name: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth label="Original Cost" type="number" required
                                value={formData.original_cost}
                                onChange={e => setFormData(prev => ({ ...prev, original_cost: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth label="Date Placed in Service" type="date" required
                                value={formData.date_placed_in_service}
                                onChange={e => setFormData(prev => ({ ...prev, date_placed_in_service: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth select label="Depreciation Method" required
                                value={formData.depreciation_method}
                                onChange={e => setFormData(prev => ({ ...prev, depreciation_method: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {DEPRECIATION_METHODS.map(m => (
                                    <MenuItem key={m.value} value={m.value} sx={{ fontFamily }}>{m.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth label="Useful Life (Years)" type="number" required
                                value={formData.useful_life_years}
                                onChange={e => setFormData(prev => ({ ...prev, useful_life_years: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth label="Salvage Value" type="number"
                                value={formData.salvage_value}
                                onChange={e => setFormData(prev => ({ ...prev, salvage_value: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth label="Section 179 Amount" type="number"
                                value={formData.section_179_amount}
                                onChange={e => setFormData(prev => ({ ...prev, section_179_amount: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth label="Bonus Depreciation %" type="number"
                                value={formData.bonus_depreciation_percent}
                                onChange={e => setFormData(prev => ({ ...prev, bonus_depreciation_percent: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', color: '#64748b', mt: 1, mb: 0.5 }}>GL Accounts</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField size="small" fullWidth label="Asset Account"
                                value={formData.gl_asset_account}
                                onChange={e => setFormData(prev => ({ ...prev, gl_asset_account: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField size="small" fullWidth label="Depreciation Account"
                                value={formData.gl_depreciation_account}
                                onChange={e => setFormData(prev => ({ ...prev, gl_depreciation_account: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField size="small" fullWidth label="Expense Account"
                                value={formData.gl_expense_account}
                                onChange={e => setFormData(prev => ({ ...prev, gl_expense_account: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={saving || !formData.asset_name || !formData.original_cost}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dispose Asset Dialog */}
            <Dialog open={disposeOpen} onClose={() => setDisposeOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Dispose Asset</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#475569', mb: 2, fontSize: '0.9rem' }}>
                        Record the disposal of this asset. This action cannot be undone.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Disposal Amount" type="number" required
                                value={disposeData.disposal_amount}
                                onChange={e => setDisposeData(prev => ({ ...prev, disposal_amount: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Date Disposed" type="date" required
                                value={disposeData.date_disposed}
                                onChange={e => setDisposeData(prev => ({ ...prev, date_disposed: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDisposeOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleDispose} variant="contained" disabled={disposing || !disposeData.disposal_amount || !disposeData.date_disposed}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                        {disposing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Dispose'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
